import React from 'react';
import PageTemplate from '../components/PageTemplate';

const CareersPage = () => {
  const openPositions = [
    {
      id: 1,
      title: 'Senior Full Stack Developer',
      department: 'Engineering',
      location: 'Remote (US)',
      type: 'Full-time',
      description: 'We are looking for an experienced Full Stack Developer to help build and improve our patient-facing web application. The ideal candidate will have experience with React, Node.js, and MongoDB.',
      responsibilities: [
        'Develop and maintain our web application using React and Node.js',
        'Work with our design team to implement user interfaces',
        'Optimize application for maximum speed and scalability',
        'Collaborate with cross-functional teams to define, design, and ship new features'
      ],
      requirements: [
        '5+ years of experience in full stack development',
        'Strong proficiency in JavaScript, including DOM manipulation and the JavaScript object model',
        'Experience with React.js and its core principles',
        'Experience with popular React.js workflows (such as Flux or Redux)',
        'Experience with Node.js and MongoDB',
        'Familiarity with RESTful APIs and modern authorization mechanisms'
      ]
    },
    {
      id: 2,
      title: 'AI Research Scientist',
      department: 'AI & Machine Learning',
      location: 'Boston, MA (Hybrid)',
      type: 'Full-time',
      description: 'Join our AI team to help develop and improve our natural language processing models for healthcare applications. You will work on cutting-edge AI technology that helps patients better communicate with their healthcare providers.',
      responsibilities: [
        'Research and implement state-of-the-art NLP techniques for healthcare applications',
        'Develop models to extract and structure medical information from patient inputs',
        'Work with medical experts to ensure accuracy and relevance of AI outputs',
        'Publish research findings in academic journals and conferences'
      ],
      requirements: [
        'Ph.D. in Computer Science, Artificial Intelligence, or related field',
        'Strong background in natural language processing and machine learning',
        'Experience with Python and deep learning frameworks (PyTorch, TensorFlow)',
        'Publications in top-tier NLP/ML conferences or journals',
        'Experience with healthcare data is a plus'
      ]
    },
    {
      id: 3,
      title: 'Medical Content Writer',
      department: 'Content',
      location: 'Remote (US)',
      type: 'Part-time',
      description: 'We are seeking a healthcare professional with strong writing skills to create accurate, accessible medical content for our platform. You will work closely with our medical and product teams to develop content that helps patients understand and communicate their health concerns.',
      responsibilities: [
        'Create clear, accurate medical content for patients',
        'Review and edit AI-generated content for medical accuracy',
        'Develop educational resources about common medical conditions and procedures',
        'Stay up-to-date with medical research and best practices'
      ],
      requirements: [
        'Medical degree (MD, DO, NP, PA) or equivalent healthcare qualification',
        'Excellent writing and editing skills',
        'Ability to explain complex medical concepts in simple terms',
        'Experience in health education or patient communication',
        'Understanding of evidence-based medicine'
      ]
    },
    {
      id: 4,
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote (US)',
      type: 'Full-time',
      description: 'Help us create intuitive, accessible user experiences for patients of all ages and technical abilities. You will design interfaces that make it easy for users to input their health information and understand the outputs.',
      responsibilities: [
        'Design user interfaces for web and mobile applications',
        'Create wireframes, prototypes, and high-fidelity mockups',
        'Conduct user research and usability testing',
        'Collaborate with developers to implement designs',
        'Ensure accessibility for users of all abilities'
      ],
      requirements: [
        '3+ years of experience in UX/UI design',
        'Strong portfolio demonstrating user-centered design process',
        'Proficiency in design tools (Figma, Sketch, Adobe XD)',
        'Experience with responsive web design',
        'Knowledge of accessibility standards',
        'Healthcare experience is a plus'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Health & Wellness',
      items: [
        'Comprehensive health, dental, and vision insurance',
        'Mental health support and resources',
        'Wellness stipend for gym memberships or fitness classes',
        'Flexible time off policy'
      ]
    },
    {
      title: 'Professional Growth',
      items: [
        'Learning and development budget',
        'Conference attendance opportunities',
        'Mentorship program',
        'Career advancement pathways'
      ]
    },
    {
      title: 'Work-Life Balance',
      items: [
        'Flexible work hours',
        'Remote work options',
        'Paid parental leave',
        '4-day work week pilot program'
      ]
    },
    {
      title: 'Financial Benefits',
      items: [
        'Competitive salary',
        'Equity options',
        '401(k) matching',
        'Quarterly performance bonuses'
      ]
    }
  ];

  return (
    <PageTemplate
      title="Careers"
      heroImage="/images/careers-hero.jpg"
      heroDescription="Join our mission to transform healthcare through technology"
    >
      <section className="mb-5">
        <h2 className="mb-4">Why Work With Us</h2>
        <div className="grid grid-cols-1 grid-md-cols-2 gap-lg">
          <div>
            <p className="mb-3">
              At Patients Empowered, we're building technology that helps patients become active participants in their healthcare journey.
              Our work directly impacts people's health outcomes and quality of life.
            </p>
            <p className="mb-3">
              We're a diverse team of healthcare professionals, technologists, and patient advocates united by our mission to make
              healthcare more accessible, efficient, and patient-centered.
            </p>
            <p>
              If you're passionate about using technology to solve real-world healthcare problems, we'd love to hear from you.
            </p>
          </div>
          <div className="card">
            <h3 className="mb-3">Our Culture</h3>
            <ul className="mb-0">
              <li className="mb-2"><strong>Mission-driven:</strong> We're united by our commitment to improving healthcare.</li>
              <li className="mb-2"><strong>Collaborative:</strong> We believe the best solutions come from diverse perspectives.</li>
              <li className="mb-2"><strong>Innovative:</strong> We're not afraid to challenge the status quo and try new approaches.</li>
              <li className="mb-2"><strong>User-focused:</strong> We put patients at the center of everything we do.</li>
              <li><strong>Growth-oriented:</strong> We invest in our team members' professional development.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-5">
        <h2 className="mb-4">Benefits & Perks</h2>
        <div className="grid grid-cols-1 grid-md-cols-2 grid-lg-cols-4 gap-md">
          {benefits.map((benefit, index) => (
            <div key={index} className="card">
              <h3 className="mb-3">{benefit.title}</h3>
              <ul className="mb-0">
                {benefit.items.map((item, i) => (
                  <li key={i} className={i < benefit.items.length - 1 ? 'mb-2' : ''}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-4">Open Positions</h2>

        <div className="mb-4">
          <div className="grid grid-cols-1 grid-md-cols-3 gap-md">
            <select className="form-control">
              <option>All Departments</option>
              <option>Engineering</option>
              <option>AI & Machine Learning</option>
              <option>Content</option>
              <option>Design</option>
            </select>

            <select className="form-control">
              <option>All Locations</option>
              <option>Remote (US)</option>
              <option>Boston, MA</option>
            </select>

            <select className="form-control">
              <option>All Types</option>
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-md">
          {openPositions.map(position => (
            <div key={position.id} className="card">
              <div className="mb-3">
                <h3 className="mb-1">{position.title}</h3>
                <div className="flex flex-wrap gap-sm">
                  <span className="badge badge-primary">{position.department}</span>
                  <span className="badge badge-secondary">{position.location}</span>
                  <span className="badge badge-accent">{position.type}</span>
                </div>
              </div>

              <p className="mb-3">{position.description}</p>

              <div className="mb-3">
                <h4 className="mb-2">Responsibilities:</h4>
                <ul>
                  {position.responsibilities.map((resp, index) => (
                    <li key={index} className="mb-1">{resp}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-3">
                <h4 className="mb-2">Requirements:</h4>
                <ul>
                  {position.requirements.map((req, index) => (
                    <li key={index} className="mb-1">{req}</li>
                  ))}
                </ul>
              </div>

              <div className="text-right">
                <a href={`/careers/${position.id}`} className="btn btn-primary">Apply Now</a>
              </div>
            </div>
          ))}
        </div>
      </section>
    </PageTemplate>
  );
};

export default CareersPage;
